import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { appConfig } from '@/api/appConfig';
import type {
  InternalUser,
  M3ApiResponse,
  ResendVerificationEmailRequest,
} from '@/types/api';

const USERS_KEY = ['internal-users'] as const;
const ME_KEY = ['internal-users', 'me'] as const;

export function useUsersQuery() {
  return useQuery({
    queryKey: USERS_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<M3ApiResponse<InternalUser[]>>(
        '/api/v1/internalUsers/users',
      );
      return data.result ?? [];
    },
  });
}

export function useCurrentUserQuery() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<M3ApiResponse<InternalUser>>(
        '/api/v1/internalUsers/me',
      );
      localStorage.setItem('ops_user', JSON.stringify(data.result));
      return data.result;
    },
  });
}

export function getCachedUser(): InternalUser | null {
  const raw = localStorage.getItem('ops_user');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as InternalUser;
  } catch {
    return null;
  }
}

export function useToggleUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; block: boolean }) => {
      const { data } = await apiClient.post<M3ApiResponse>(
        `${appConfig.userAccountApi}/blockUnblockUser`,
        { userId: input.userId, blocked: input.block },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEY }),
  });
}

export function useAddUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      username: string;
      firstname: string;
      lastname: string;
      profile: string;
    }) => {
      const { data } = await apiClient.post<M3ApiResponse>(
        '/api/v1/internalUsers/users',
        input,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEY }),
  });
}

export function useUpdateUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      userId: string;
      firstname: string;
      lastname: string;
    }) => {
      const { data } = await apiClient.put<M3ApiResponse>(
        '/api/v1/internalUsers/changePersonalInfo',
        input,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
      qc.invalidateQueries({ queryKey: ME_KEY });
    },
  });
}

export function useChangePasswordMutation() {
  return useMutation({
    mutationFn: async (input: {
      username: string;
      newPassword: string;
      previousPassword: string;
    }) => {
      const { data } = await apiClient.post<M3ApiResponse>(
        `${appConfig.userAccountApi}/changePassword`,
        {
          username: `${input.username}@diool.com`,
          newPassword: input.newPassword,
          previousPassword: input.previousPassword,
          internalUser: true,
        },
      );
      return data;
    },
  });
}

export function useChangeLanguageMutation() {
  return useMutation({
    mutationFn: async (lang: string) => {
      const { data } = await apiClient.post<M3ApiResponse>(
        `${appConfig.userAccountApi}/changeUserLanguage`,
        { lang },
      );
      return data;
    },
  });
}

export function useChangeProfilePictureMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await apiClient.post<M3ApiResponse>(
        `${appConfig.userAccountApi}/changeUserPicture`,
        formData,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_KEY }),
  });
}

export function useResendVerificationEmailMutation() {
  return useMutation({
    mutationFn: async (input: ResendVerificationEmailRequest) => {
      const { data } = await apiClient.post<M3ApiResponse>(
        `${appConfig.userAccountApi}/resendVerificationEmail`,
        input,
      );
      return data;
    },
  });
}
